import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { FileText, Loader2, X, Mail, ShieldCheck } from "lucide-react";
import { useLocale } from "@/i18n/useLocale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "spiruva.lead.v1";

// Frontend mirror of the backend blocklist — fast UX; backend is the source of truth.
const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com","googlemail.com",
  "yahoo.com","yahoo.co.uk","yahoo.co.in","yahoo.fr","yahoo.de","yahoo.es","yahoo.it","yahoo.ca","yahoo.com.au","yahoo.co.jp","ymail.com","rocketmail.com",
  "hotmail.com","hotmail.co.uk","hotmail.fr","hotmail.de","hotmail.it","hotmail.es","hotmail.com.br",
  "outlook.com","outlook.fr","outlook.de","outlook.es","outlook.it","outlook.co.uk","live.com","live.co.uk","msn.com",
  "icloud.com","me.com","mac.com","aol.com","aim.com",
  "proton.me","protonmail.com","pm.me",
  "gmx.com","gmx.de","gmx.net","gmx.at",
  "mail.com","email.com","zoho.com","yandex.com","yandex.ru",
  "qq.com","163.com","126.com","sina.com","sina.cn","sohu.com",
  "rediffmail.com","fastmail.com","tutanota.com","tuta.io",
  "t-online.de","web.de","freenet.de","arcor.de",
  "orange.fr","wanadoo.fr","free.fr","laposte.net","sfr.fr",
  "libero.it","tiscali.it","virgilio.it","alice.it",
  "bigpond.com","bigpond.net.au","btinternet.com","sky.com","talktalk.net","ntlworld.com",
  "comcast.net","verizon.net","att.net","bellsouth.net","sbcglobal.net","cox.net","earthlink.net",
  "shaw.ca","rogers.com","telus.net",
  "docomo.ne.jp","softbank.ne.jp","ezweb.ne.jp","i.softbank.jp","nifty.com","biglobe.ne.jp","ocn.ne.jp","so-net.ne.jp",
  "mailinator.com","mailinator.net","10minutemail.com","guerrillamail.com","guerrillamail.net","guerrillamail.org",
  "sharklasers.com","yopmail.com","trashmail.com","throwawaymail.com","getnada.com","nada.email",
  "tempmail.com","temp-mail.org","temp-mail.io","tempinbox.com","dispostable.com",
  "maildrop.cc","mintemail.com","moakt.com","fakeinbox.com","tempr.email",
  "emailondeck.com","spamgourmet.com","mytemp.email","anonbox.net","discard.email",
]);

const isFreeEmail = (email) => {
  if (!email || !email.includes("@")) return false;
  const domain = email.split("@").pop().toLowerCase().trim();
  if (!domain) return false;
  if (FREE_EMAIL_DOMAINS.has(domain)) return true;
  const parts = domain.split(".");
  for (let i = 1; i < parts.length; i++) {
    if (FREE_EMAIL_DOMAINS.has(parts.slice(i).join("."))) return true;
  }
  return false;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email || "");

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStored = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota or private mode — ignore */
  }
};

const INTENT_KEYS = [
  ["evaluation", "intent_evaluation"],
  ["procurement", "intent_procurement"],
  ["rd", "intent_rd"],
  ["distribution", "intent_distribution"],
  ["other", "intent_other"],
];

/**
 * DownloadGate — controlled modal that captures a B2B lead before triggering
 * a PDF download. Always shown (prefilled if returning user).
 */
const DownloadGate = ({ open, onClose, resource }) => {
  const { t } = useTranslation();
  const { locale, L } = useLocale();
  const [hasStored, setHasStored] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    company: "",
    intent: "evaluation",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [ttlHours, setTtlHours] = useState(48);

  // Prefill on open
  useEffect(() => {
    if (!open) return;
    const stored = readStored();
    setDone(false);
    setErrors({});
    setHasStored(!!stored?.email);
    setForm({
      full_name: stored?.full_name || "",
      email: stored?.email || "",
      mobile: stored?.mobile || "",
      company: stored?.company || "",
      intent: stored?.intent || "evaluation",
      consent: false, // GDPR: must explicitly re-confirm consent every session
    });
  }, [open]);

  // ESC + body lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = t("download_gate.error_name");
    if (!isValidEmail(form.email)) e.email = t("download_gate.error_email");
    else if (isFreeEmail(form.email)) e.email = t("download_gate.error_free_email");
    if (!form.mobile.trim()) e.mobile = t("download_gate.error_mobile");
    if (!form.company.trim()) e.company = t("download_gate.error_company");
    if (!form.intent) e.intent = t("download_gate.error_intent");
    if (!form.consent) e.consent = t("download_gate.error_consent");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!resource || !validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        company: form.company.trim(),
        intent: form.intent,
        consent: !!form.consent,
        resource_kind: resource.kind,
        resource_id: resource.id,
        resource_title: resource.title || "",
        locale,
      };
      const { data } = await axios.post(`${API}/leads`, payload);
      // Persist for prefill (consent intentionally not stored — must re-confirm each session)
      writeStored({
        full_name: payload.full_name,
        email: payload.email,
        mobile: payload.mobile,
        company: payload.company,
        intent: payload.intent,
        savedAt: new Date().toISOString(),
      });
      setSentTo(data?.email || payload.email);
      setTtlHours(data?.expires_in_hours || 48);
      setDone(true);
      toast.success(t("download_gate.success_title"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "free_email_blocked") {
        setErrors((e) => ({ ...e, email: t("download_gate.error_free_email") }));
      } else if (detail?.code === "consent_required") {
        setErrors((e) => ({ ...e, consent: t("download_gate.error_consent") }));
      } else {
        toast.error(t("download_gate.error_submit"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-brand-black/70 z-[80] flex items-center justify-center p-4 md:p-6"
      data-testid="download-gate-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white max-w-xl w-full p-7 md:p-9 border border-brand-green/15 relative max-h-[92vh] overflow-y-auto">
        <button
          onClick={() => onClose?.()}
          aria-label={t("download_gate.cancel")}
          data-testid="download-gate-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-brand-black/55 hover:text-brand-black"
        >
          <X size={18} />
        </button>

        {!done ? (
          <>
            <div className="w-12 h-12 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40 flex items-center justify-center mb-5">
              <FileText size={18} />
            </div>
            <p className="eyebrow mb-2">◦  Premium PDF</p>
            <h2 className="font-display text-2xl md:text-3xl text-brand-black leading-tight" data-testid="download-gate-title">
              {t("download_gate.title")}
            </h2>
            <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">
              {t("download_gate.subtitle")}
            </p>

            {resource?.title && (
              <div className="mt-5 px-4 py-3 bg-brand-cream/50 border-l-2 border-brand-gold">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55 mb-1">{t("download_gate.asset_label")}</p>
                <p className="font-display text-sm text-brand-black leading-snug" data-testid="download-gate-asset">{resource.title}</p>
              </div>
            )}

            {hasStored && (
              <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.18em] text-brand-teal" data-testid="download-gate-prefill-note">
                ◦  {t("download_gate.prefill_note")}
              </p>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="download-gate-form" noValidate>
              <Field
                label={t("download_gate.field_name")}
                value={form.full_name}
                onChange={(v) => setField("full_name", v)}
                error={errors.full_name}
                testid="download-gate-name"
              />
              <Field
                label={t("download_gate.field_email")}
                value={form.email}
                onChange={(v) => setField("email", v)}
                error={errors.email}
                type="email"
                testid="download-gate-email"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label={t("download_gate.field_mobile")}
                  value={form.mobile}
                  onChange={(v) => setField("mobile", v)}
                  error={errors.mobile}
                  testid="download-gate-mobile"
                />
                <Field
                  label={t("download_gate.field_company")}
                  value={form.company}
                  onChange={(v) => setField("company", v)}
                  error={errors.company}
                  testid="download-gate-company"
                />
              </div>

              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{t("download_gate.field_intent")}</span>
                <select
                  value={form.intent}
                  onChange={(e) => setField("intent", e.target.value)}
                  data-testid="download-gate-intent"
                  className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm font-body"
                >
                  {INTENT_KEYS.map(([val, key]) => (
                    <option key={val} value={val}>{t(`download_gate.${key}`)}</option>
                  ))}
                </select>
                {errors.intent && <p className="mt-1 text-xs text-red-600" data-testid="download-gate-intent-error">{errors.intent}</p>}
              </div>

              {/* GDPR consent */}
              <label
                className={`flex items-start gap-3 mt-2 cursor-pointer select-none p-3 border ${errors.consent ? "border-red-400" : "border-brand-green/15"} bg-brand-cream/30`}
                data-testid="download-gate-consent-label"
              >
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setField("consent", e.target.checked)}
                  data-testid="download-gate-consent"
                  className="mt-1 w-4 h-4 accent-brand-green shrink-0"
                />
                <span className="text-xs text-brand-black/80 leading-relaxed">
                  {t("download_gate.consent_label")}
                  <br />
                  <span className="text-[11px] text-brand-black/60">
                    {t("download_gate.consent_privacy_prefix")}{" "}
                    <Link
                      to={L("/privacy")}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="download-gate-privacy-link"
                      className="underline hover:text-brand-teal"
                    >
                      {t("download_gate.consent_privacy_link")}
                    </Link>{" "}
                    {t("download_gate.consent_privacy_suffix")}
                  </span>
                </span>
              </label>
              {errors.consent && <p className="text-xs text-red-600" data-testid="download-gate-consent-error">{errors.consent}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="download-gate-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-5 py-3.5 text-xs uppercase tracking-[0.22em] font-medium hover:bg-brand-black hover:text-brand-cream transition disabled:opacity-60 mt-1"
              >
                {submitting ? (
                  <><Loader2 size={14} className="animate-spin" /> {t("download_gate.submitting")}</>
                ) : (
                  <><Mail size={14} /> {t("download_gate.submit")}</>
                )}
              </button>

              <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-brand-black/45 pt-2">
                <ShieldCheck size={11} /> {t("download_gate.privacy")}
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="download-gate-success">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-green text-brand-cream mb-5">
              <Mail size={22} />
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-brand-black leading-tight" data-testid="download-gate-success-title">
              {t("download_gate.success_title")}
            </h2>
            <p className="text-sm text-brand-black/75 mt-4 leading-relaxed" data-testid="download-gate-success-body">
              <Trans
                i18nKey="download_gate.success_body"
                values={{ email: sentTo, hours: ttlHours }}
                components={{ strong: <strong className="text-brand-black" /> }}
              />
            </p>
            <p className="text-xs text-brand-black/55 mt-4 leading-relaxed">
              {t("download_gate.success_hint_no_email")}
            </p>
            <button
              onClick={() => onClose?.()}
              data-testid="download-gate-done-close"
              className="mt-7 inline-flex items-center justify-center gap-2 border border-brand-green/30 text-brand-black px-5 py-3 text-xs uppercase tracking-[0.22em] hover:bg-brand-green hover:text-brand-cream transition"
            >
              {t("download_gate.success_request_another")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, error }) => (
  <div>
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border ${error ? "border-red-400" : "border-brand-green/20"} focus:border-brand-teal focus:outline-none text-sm font-body`}
    />
    {error && <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>}
  </div>
);

export default DownloadGate;
